// src/components/Hero.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeroSection = styled.section`
    min-height: 100vh;
    display: flex;
    align-items: center;
    position: relative;
    background:
            linear-gradient(135deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4)),
            url('/tatras.jpg') center/cover;

    @media (max-width: 768px) {
        background:
                linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.5)),
                url('/tatras.jpg') center/cover;
    }
`;

const HeroContent = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 2rem;
    width: 100%;
    position: relative;
    z-index: 2;
`;

const HeroText = styled.div`
    color: white;
    max-width: 600px;

    h1 {
        font-size: 4rem;
        font-weight: 800;
        line-height: 1.1;
        margin-bottom: 1.5rem;

        @media (max-width: 768px) {
            font-size: 3rem;
        }
    }
`;

const HeroSubtitle = styled.p`
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
    margin-bottom: 2.5rem;
    font-weight: 400;
`;

const CTAContainer = styled(motion.div)`
    display: flex;
    gap: 1.5rem;
    align-items: center;
    flex-wrap: wrap;
`;

const PrimaryButton = styled(motion.button)`
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border: none;
    padding: 1.125rem 2.5rem;
    border-radius: 10px;
    font-size: 1.125rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.3);

    &:hover {
        transform: translateY(-3px);
        box-shadow: 0 12px 35px rgba(102, 126, 234, 0.4);
    }
`;

const SecondaryButton = styled(motion.button)`
    background: transparent;
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    padding: 1.125rem 2.5rem;
    border-radius: 10px;
    font-size: 1.125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.5);
    }
`;

interface HeroProps {
    onNavigateToTrips: () => void;
}

const Hero: React.FC<HeroProps> = ({ onNavigateToTrips }) => {
    return (
        <HeroSection>
            <HeroContent>
                <HeroText>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        Discover the heart of Europe's Hidden Treasures
                    </motion.h1>
                    <HeroSubtitle>
                        Tours through Austria, Slovakia, Czech Republic and Hungary.
                        Experience unforgettable journeys with personalized service and expert guides.
                    </HeroSubtitle>
                    <CTAContainer
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        <PrimaryButton
                            onClick={onNavigateToTrips}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Explore Tours
                        </PrimaryButton>
                        <SecondaryButton
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Learn More
                        </SecondaryButton>
                    </CTAContainer>
                </HeroText>
            </HeroContent>
        </HeroSection>
    );
};

export default Hero;